import { AssetClassesQuery } from '@/__generated__/graphql';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import Input from '@/components/basicComponents/input';
import { formatPercentageInputValue, handleCursorPositionInPercentageInput } from '@/components/basicComponents/input/utils';
import { useResponsive } from '@/hooks/use-responsive';
import { useStatusData } from '@/hooks/useStatusData';
import { themeVar } from '@/styles/theme';
import { useEffect, useState } from 'react';
import { Control, Controller, useWatch } from 'react-hook-form';
import styled from 'styled-components';

interface EditAssetClassTargetsProps {
  control: Control<any>;
  assetClasses: AssetClassesQuery['assetClasses'];
}

export const EditAssetClassTargets = ({ control, assetClasses }: EditAssetClassTargetsProps) => {
  const { isMobile } = useResponsive();
  const { data: statusData } = useStatusData();
  const allocationTargets: Record<string, string> = useWatch({ control, name: 'allocationTargets' });

  const [withSubAssetClasses, setWithSubAssetClasses] = useState<boolean>(false);

  useEffect(() => {
    const hasSubAssetClasses = assetClasses.some((assetClass) => assetClass?.subAssetClasses?.length > 0);
    setWithSubAssetClasses(hasSubAssetClasses);
  }, [assetClasses]);

  const privateCapitalTotal = Object.values(allocationTargets).reduce((acc, curr) => acc + parseFloat(curr || '0'), 0);
  const subAssetClassTotals: Record<string, string> = assetClasses.reduce((acc, assetClass) => {
    const subAssetClassTotal = assetClass.subAssetClasses.reduce((sum, curr) => sum + parseFloat(allocationTargets[curr.id] || '0'), 0);

    return {
      ...acc,
      [assetClass.id]: `${subAssetClassTotal.toFixed(2)}%`
    };
  }, {});

  return (
    <div>
      <PrivateCapitalTotal>
        <AssetClassTitle>PRIVATE CAPITAL TOTAL</AssetClassTitle>
        <AssetClassTotal>{`${privateCapitalTotal.toFixed(2)}%`}</AssetClassTotal>
      </PrivateCapitalTotal>
      <AssetClassTargetsWrapper>
        {statusData?.target_sub_asset_classes && withSubAssetClasses ? (
          <WithSubAssetClassesWrapper>
            {assetClasses.map((assetClass) => (
              <div key={assetClass.id}>
                <AssetClassHeaderWrap>
                  <AssetClassTitle>{assetClass.name}</AssetClassTitle>
                  <AssetClassTotal>{subAssetClassTotals?.[assetClass?.id] || '0.00%'}</AssetClassTotal>
                </AssetClassHeaderWrap>
                <AssetClassTableBody>
                  {assetClass.subAssetClasses.map((subAssetClass) => (
                    <AssetClassTableRow key={subAssetClass.id} isMobile={isMobile}>
                      <AssetClassTitle>{subAssetClass.name}</AssetClassTitle>
                      <Controller
                        name={`allocationTargets.${subAssetClass.id}`}
                        control={control}
                        rules={{
                          required: {
                            value: true,
                            message: 'This field is required'
                          }
                        }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <Input
                            value={value || ''}
                            onChange={(e) => {
                              const formattedValue = formatPercentageInputValue(e);
                              onChange(formattedValue);
                            }}
                            onClick={handleCursorPositionInPercentageInput}
                            onKeyUp={handleCursorPositionInPercentageInput}
                            size="md"
                            width={isMobile ? '100%' : '225px'}
                            errorText={error?.message}
                          />
                        )}
                      />
                    </AssetClassTableRow>
                  ))}
                </AssetClassTableBody>
              </div>
            ))}
          </WithSubAssetClassesWrapper>
        ) : (
          <div>
            <AssetClassTableBody>
              {assetClasses.map((assetClass) => (
                <AssetClassTableRow key={assetClass.id} isMobile={isMobile}>
                  <AssetClassTitle>{assetClass.name}</AssetClassTitle>
                  <Controller
                    name={`allocationTargets.${assetClass.id}`}
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: 'This field is required'
                      }
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Input
                        value={value || ''}
                        onChange={(e) => {
                          const formattedValue = formatPercentageInputValue(e);
                          onChange(formattedValue);
                        }}
                        onClick={handleCursorPositionInPercentageInput}
                        onKeyUp={handleCursorPositionInPercentageInput}
                        size="md"
                        width={isMobile ? '100%' : '225px'}
                        errorText={error?.message}
                      />
                    )}
                  />
                </AssetClassTableRow>
              ))}
            </AssetClassTableBody>
          </div>
        )}

        <AssetClassBannerWrap isMobile={isMobile}>
          <AssetClassBanner>
            <Banner
              title={privateCapitalTotal.toFixed(2) + '%'}
              description="Private Capital targets must not exceed 100%"
              icon={
                privateCapitalTotal <= 100 ? (
                  <ApprovedIcon fill={themeVar('context.light')} />
                ) : (
                  <RejectedBannerIcon fill={themeVar('context.light')} />
                )
              }
              bgColor={privateCapitalTotal <= 100 ? themeVar('context.success') : themeVar('context.error')}
            />
          </AssetClassBanner>
        </AssetClassBannerWrap>
      </AssetClassTargetsWrapper>
    </div>
  );
};

const PrivateCapitalTotal = styled.div`
  display: flex;
  align-items: center;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  padding: 10px 30px;
  margin-top: 25px;
  border-radius: 4px;
  background: ${themeVar('layer.3')};
  color: ${themeVar('header.action')};
`;

const WithSubAssetClassesWrapper = styled.div`
  margin-top: 30px;
  display: flex;
  flex-direction: column;
  gap: 50px;
`;

const AssetClassTargetsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 50px;
`;

const AssetClassTableBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 30px;
`;

const AssetClassTableRow = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: ${({ isMobile }) => (isMobile ? 'flex-start' : 'center')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
`;

const AssetClassHeaderWrap = styled.div`
  padding: 10px 30px;
  background-color: ${themeVar('layer.2')};
  display: flex;
  align-items: center;
`;

const AssetClassTitle = styled.div`
  max-width: 228px;
  width: 100%;
`;

const AssetClassTotal = styled.div`
  font-weight: 700;
`;

const AssetClassBannerWrap = styled.div<{ isMobile: boolean }>`
  padding-left: ${({ isMobile }) => (isMobile ? '0' : '185px')};
`;

const AssetClassBanner = styled.div`
  max-width: 341px;
  width: 100%;
`;
